/**
 * @module ServiceTestsStaticColumns
 * @description Phase 12 extraction of the static Service Test list column builders.
 * Keeps reusable base columns separate from dynamic test-type column composition.
 */

import { t } from '@lingui/macro';
import { get, some } from 'lodash';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { buildPageTargetWithPathAndIdProp } from '../AdminLinkUtils';
import {
    ADAPTER_TYPE_FIELD_NAME,
    SCHEDULING_FIELD_NAME,
    SERVICE_TESTS_FUNCTION_NAME,
    TEST_BUSINESS_SERVICES_FIELD,
    TEST_NAME_FIELD,
    TEST_STATUS_FIELD,
    TEST_TAGS_FIELD,
    TYPE_VOIP,
} from './ServiceTestsConstants';
import {
    formatAdapterType,
    formatScheduleTime,
    formatScheduleType,
    formatTestInterval,
} from './formatters/ScheduleFormatters';
import { BusinessServicesCell } from './formatters/BusinessServicesFormatter';
import { DEFAULT_ID_PROPERTY } from '../AdminPages';
import { formatTags } from '../../../utilities/StringUtilities';
import { MaterialUiIcon } from '../../icon';

const TYPE_GROUP_TITLE_MAPPING = 'testTypeObject.details.title';
const TYPE_GROUP_ICON_MAPPING = 'testTypeObject.details.icon';

export function getTypeIconColumn() {
    return {
        type: COLUMN_TYPES.ICON,
        mapping: TYPE_GROUP_ICON_MAPPING,
        title: t`Type`,
        sortProperty: TYPE_GROUP_TITLE_MAPPING,
        sortable: true,
        width: 80,
        icons: [{ getIcon: (row) => <MaterialUiIcon icon={get(row, TYPE_GROUP_ICON_MAPPING)} /> }],
        getTooltip: (row) => get(row, TYPE_GROUP_TITLE_MAPPING),
        csvFormatter: (_value, row) => get(row, TYPE_GROUP_TITLE_MAPPING),
    };
}

export function getNameColumn(group) {
    const encodeGroup = encodeURIComponent(group);
    return {
        type: COLUMN_TYPES.LINK,
        mapping: TEST_NAME_FIELD,
        title: t`Name`,
        sortable: true,
        width: 250,
        link: {
            sysAdminOnly: true,
            target: buildPageTargetWithPathAndIdProp(
                SERVICE_TESTS_FUNCTION_NAME,
                `/${encodeGroup}/tests/`,
                DEFAULT_ID_PROPERTY
            ),
        },
    };
}

export function getStatusColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: TEST_STATUS_FIELD,
        title: t`Status`,
        sortable: true,
        width: 120,
    };
}

export function getScheduleTypeColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: SCHEDULING_FIELD_NAME,
        formatter: formatScheduleType,
        title: t`Run Schedule`,
        tooltip: t`Schedule type (Daily/Window/Interval)`,
        sortable: true,
        width: 130,
    };
}

export function getInterfaceColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: { ADAPTER_TYPE_FIELD_NAME },
        title: t`Interface`,
        tooltip: null,
        sortable: true,
        formatter: formatAdapterType,
        width: 160,
    };
}

export function shouldShowInterfaceColumn(testTypeArrayObject) {
    return some(
        testTypeArrayObject,
        (testTypeObject) => testTypeObject?.supportsInterfaceSelection === true && testTypeObject?.group !== TYPE_VOIP
    );
}

export function getScheduleIntervalColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: SCHEDULING_FIELD_NAME,
        formatter: formatTestInterval,
        title: t`Run Interval`,
        tooltip: t`Interval between test executions`,
        sortable: true,
        width: 120,
    };
}

export function getScheduleTimeColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: SCHEDULING_FIELD_NAME,
        formatter: formatScheduleTime,
        title: t`Run Time`,
        tooltip: t`Scheduled run time in your timezone`,
        sortable: true,
        width: 150,
    };
}

export function getBusinessServicesColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: TEST_BUSINESS_SERVICES_FIELD,
        title: t`Business Services`,
        tooltip: null,
        sortable: true,
        sortProperty: 'businessServicesSortable',
        width: 210,
        csvFormatter: (value) => {
            if (!value || value.length === 0) {
                return '';
            }
            return value
                .map((bs) => bs.name || 'N/A')
                .filter(Boolean)
                .join(', ');
        },
        renderer: (row, column) => {
            const businessServices = column.mapper(row, column);
            return <BusinessServicesCell businessServices={businessServices} />;
        },
    };
}

export function getTagsColumn() {
    return {
        type: COLUMN_TYPES.TEXT,
        mapping: TEST_TAGS_FIELD,
        title: t`Tags`,
        sortable: true,
        formatter: formatTags,
        width: 200,
    };
}

/**
 * Mapping of testConfig field names to display titles
 * Used to override auto-generated titles for dynamic columns
 */
export const TEST_CONFIG_TITLE_MAP = {
    url: t`URL`,
    phone: t`Number to Call`,
    hostname: t`Target`,
    desired_status: t`Desired Status`,
    host: t`Target`,
    dnsServer: t`DNS Server`,
    domainToResolve: t`Domain to Resolve`,
    ftp_server: t`Target`,
    server: t`Target`,
};

/**
 * Get custom title for testConfig fields
 * @param {string} mapping - Column mapping (e.g., 'testConfig.url')
 * @returns {string|null} Custom title or null if no custom title exists
 */
export function getTestConfigTitle(mapping) {
    if (!mapping?.startsWith('testConfig.')) {
        return null;
    }
    const testTitleName = mapping.split('.')[1];
    return TEST_CONFIG_TITLE_MAP[testTitleName] || null;
}
